// Report look: colours and spacing live here so a later visual polish only
// touches this file. Class names are written out in full so Tailwind can see them.
// Channel colours match channelColors.js: blue = ads, amber = map pack,
// emerald = organic, orange = Shopping (not purple: purple is the brand colour).

export const REPORT = {
  screen: "h-full flex flex-col min-h-0 bg-violet-50/[0.15]",
  header: "shrink-0 bg-white border-b border-slate-200 px-5 pt-2.5",
  bar: "flex items-start justify-between gap-6",
  titleRow: "flex items-center gap-2.5 min-w-0",
  title: "text-xl font-bold text-slate-900 uppercase tracking-wide truncate",
  jobId: "text-xs text-slate-400 shrink-0",
  // "Market Snapshot · 120 searches · 2 hours", then the status pill and #id.
  snapshotRow: "flex items-center gap-2.5 min-w-0 mt-0.5",
  snapshotTitle: "text-sm font-semibold text-slate-700 truncate",
  subLine: "text-[0.8125rem] text-slate-500 mt-0.5",
  subStrong: "font-semibold text-slate-900",
  filters: "flex items-end gap-3 shrink-0",
  field: "flex flex-col",
  fieldLabel: "text-[0.625rem] font-semibold uppercase tracking-wider text-slate-500 mb-0.5",
  select: "w-40 border border-slate-300 rounded-lg px-2 py-1.5 text-sm text-slate-700 bg-white focus:outline-none focus:border-slate-500",
  multiButton: "w-40 flex items-center justify-between gap-2 border border-slate-300 rounded-lg px-2 py-1.5 text-sm text-slate-700 text-left bg-white focus:outline-none focus:border-slate-500",
  multiPanel: "absolute right-0 top-full mt-1 z-[1000] w-60 max-h-80 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-md py-1",
  // The Advertisers filter: a wider panel with a search box that stays at the top.
  multiPanelWide: "absolute right-0 top-full mt-1 z-[1000] w-80 max-h-96 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-md pb-1",
  multiSearchRow: "sticky top-0 z-10 bg-white px-2 pt-2 pb-1",
  multiSearch: "w-full border border-slate-300 rounded-md px-2 py-1 text-sm text-slate-700 focus:outline-none focus:border-violet-500",
  multiOption: "flex items-center gap-2 px-3 py-1 text-sm text-slate-700 hover:bg-violet-100 cursor-pointer",
  // An option with a second button (the Advertisers highlighter) at the right.
  multiOptionRow: "flex items-center gap-2 pr-3 hover:bg-violet-100",
  multiOptionLabel: "flex-1 min-w-0 flex items-center gap-2 pl-3 py-1 text-sm text-slate-700 cursor-pointer",
  segmentGroup: "flex border border-slate-300 rounded-lg overflow-hidden bg-white",
  segment: "px-4 py-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 border-l border-slate-200 first:border-l-0",
  segmentActive: "px-4 py-1.5 text-sm font-semibold bg-violet-100 text-violet-800",
  // Page tabs: folder-style tabs sitting on the header's bottom line; the open
  // tab joins the page below it (soft purple); a tab that isn't open is white
  // (no grey-blue). A tab that can't be opened is pale and dashed.
  tabRow: "flex items-end gap-1 mt-2 -mb-px",
  // Tabs never shrink or wrap, so they're the same size on every page; the
  // pin chip at the right end gives way instead.
  tab: "shrink-0 whitespace-nowrap flex items-center gap-1.5 px-4 py-1.5 text-sm text-slate-600 bg-white border border-slate-200 rounded-t-lg hover:border-violet-300 hover:text-slate-900",
  tabActive: "shrink-0 whitespace-nowrap flex items-center gap-1.5 px-4 py-1.5 text-sm font-semibold text-slate-900 bg-violet-50/[0.15] border border-slate-200 border-b-transparent rounded-t-lg",
  tabDisabled: "shrink-0 whitespace-nowrap flex items-center gap-1.5 px-4 py-1.5 text-sm text-slate-300 bg-white border border-dashed border-slate-200 border-b-slate-200 rounded-t-lg cursor-not-allowed",
  tabRight: "ml-auto self-center pb-1 pl-3 min-w-0 flex justify-end",
  pageArea: "flex-1 min-h-0 px-5 py-3",
  pageScroll: "h-full overflow-y-auto",
  loading: "h-full flex items-center justify-center text-sm text-slate-400",
};

// Job status: one purple scale, strongest for Complete (Scott, 15 Oct 2026).
// Error (Oxylabs refused the scan) is red, so it stands out.
export const STATUS_PILL = {
  error: "text-xs font-semibold bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 rounded-full shrink-0",
  complete: "text-xs font-semibold bg-violet-200 text-violet-900 border border-violet-300 px-2 py-0.5 rounded-full shrink-0",
  in_progress: "text-xs font-semibold bg-violet-100 text-violet-800 border border-violet-200 px-2 py-0.5 rounded-full shrink-0",
  stopped: "text-xs font-semibold bg-violet-50 text-violet-700 border border-dashed border-violet-300 px-2 py-0.5 rounded-full shrink-0",
};

// Channel tabs, filled with the channel's colour - the same fills as the
// Google page layout tiles on Market Overview (TILES). The open tab is a
// shade deeper, with a stronger border, bold text, and joins the page.
const CHANNEL_TAB_BASE = "shrink-0 whitespace-nowrap flex items-center px-4 py-1.5 text-sm border rounded-t-lg";
export const CHANNEL_TAB = {
  ppc: {
    tab: `${CHANNEL_TAB_BASE} bg-blue-50 border-blue-200 text-blue-900 hover:bg-blue-100`,
    active: `${CHANNEL_TAB_BASE} font-semibold bg-blue-100 border-blue-400 border-b-blue-100 text-blue-950`,
  },
  shopping: {
    tab: `${CHANNEL_TAB_BASE} bg-orange-50 border-orange-200 text-orange-900 hover:bg-orange-100`,
    active: `${CHANNEL_TAB_BASE} font-semibold bg-orange-100 border-orange-400 border-b-orange-100 text-orange-950`,
  },
  local: {
    tab: `${CHANNEL_TAB_BASE} bg-amber-50 border-amber-200 text-amber-900 hover:bg-amber-100`,
    active: `${CHANNEL_TAB_BASE} font-semibold bg-amber-100 border-amber-400 border-b-amber-100 text-amber-950`,
  },
  seo: {
    tab: `${CHANNEL_TAB_BASE} bg-emerald-50 border-emerald-200 text-emerald-900 hover:bg-emerald-100`,
    active: `${CHANNEL_TAB_BASE} font-semibold bg-emerald-100 border-emerald-400 border-b-emerald-100 text-emerald-950`,
  },
  lsa: {
    tab: `${CHANNEL_TAB_BASE} bg-rose-50 border-rose-200 text-rose-900 hover:bg-rose-100`,
    active: `${CHANNEL_TAB_BASE} font-semibold bg-rose-100 border-rose-400 border-b-rose-100 text-rose-950`,
  },
};

// Every tile and panel title on every report page (Scott, 15 Oct 2026: one
// format everywhere, like BENCHMARKS and GOOGLE PAGE LAYOUT).
const TITLE = "text-[0.6875rem] font-semibold text-slate-600 uppercase tracking-wider";

// Market Overview page. The page scrolls; panels and the table scroll inside themselves.
export const OVERVIEW = {
  page: "flex flex-col gap-2.5 pb-4",
  topRow: "grid grid-cols-3 gap-2.5",
  // The same row without the Benchmarks card (accounts that can't see AdTao).
  topRowTwo: "grid grid-cols-2 gap-2.5",
  panelRow: "grid grid-cols-3 gap-2.5 h-[23.75rem]",
  panelSlot: "flex flex-col min-h-0 min-w-0",
  tableSlot: "flex flex-col h-[80vh] min-h-[30rem]",
  card: "bg-white border border-slate-200 rounded-xl px-4 py-2.5",
  dashedCard: "bg-white border border-dashed border-slate-300 rounded-xl px-4 py-2.5",
  cardTitle: TITLE,
  cardText: "text-xs text-slate-500",
  link: "text-xs font-medium text-blue-700 underline underline-offset-2 hover:text-blue-900",
  factLabel: `${TITLE} shrink-0 mr-1`,
  fact: "text-xs text-slate-500 bg-white border border-slate-200 rounded-full px-3 py-1 whitespace-nowrap",
  factValue: "font-semibold text-slate-900",
  devButton: "ml-auto shrink-0 text-[0.6875rem] font-medium text-slate-500 border border-dashed border-slate-300 rounded px-2 py-0.5 hover:text-slate-800",
  panel: "bg-white border border-slate-200 rounded-xl flex flex-col min-h-0",
  panelHeader: "shrink-0 flex items-center justify-between gap-2 px-4 py-2",
  panelTitleRow: "flex items-baseline gap-2 min-w-0",
  panelTitle: `${TITLE} shrink-0`,
  panelNote: "text-xs text-slate-500 truncate",
  panelScroll: "flex-1 min-h-0 overflow-auto",
  panelFooter: "shrink-0 px-4 py-1.5 border-t border-slate-100 text-[0.6875rem] text-slate-500",
  th: "sticky top-0 z-10 bg-white text-xs font-normal text-slate-500 px-2 py-2 text-left align-bottom whitespace-nowrap border-y border-slate-100",
  thFirst: "sticky top-0 z-10 bg-white text-xs font-normal text-slate-500 pl-4 pr-2 py-2 text-left align-bottom whitespace-nowrap border-y border-slate-100",
  // Centred count columns (Mobile, Desktop, Keywords, Locations).
  thCenter: "sticky top-0 z-10 bg-white text-xs font-normal text-slate-500 px-2 py-2 text-center align-bottom whitespace-nowrap border-y border-slate-100",
  tdCenter: "px-2 py-2 text-sm text-slate-600 text-center whitespace-nowrap border-b border-slate-100",
  // Two-level headings: the channel name (top row) over "Seen" / "Avg pos" (second row).
  thGroup: "sticky top-0 z-10 bg-white h-8 px-2 text-xs font-semibold text-center whitespace-nowrap border-t border-slate-100",
  sort: "inline-flex items-center gap-1 hover:text-slate-900",
  sortActive: "inline-flex items-center gap-1 font-semibold text-slate-900",
  thSub: "sticky top-8 z-10 bg-white h-7 px-2 text-[0.6875rem] font-normal text-slate-500 text-left whitespace-nowrap border-b border-slate-100",
  td: "px-2 py-2 text-sm text-slate-700 whitespace-nowrap border-b border-slate-100",
  tdFirst: "pl-4 pr-2 py-2 text-sm text-slate-500 whitespace-nowrap border-b border-slate-100",
  tag: "text-[0.625rem] text-slate-600 border border-slate-300 rounded px-1 leading-4 shrink-0",
  // Visibility in Who shows up most: the toned-back purple bar; the % in the
  // same dark text as Competition type's share %.
  visBarTrack: "w-16 h-1.5 bg-violet-100 rounded-full overflow-hidden shrink-0",
  visBarFill: "h-full bg-violet-400 rounded-full",
  visOf: "w-[4.5rem] text-right text-xs text-slate-400",
  visScore: "w-10 text-right text-sm font-semibold text-slate-900",
  badge: "inline-block px-1.5 py-0.5 rounded text-xs font-medium",
  muted: "text-slate-400",
  menu: "fixed z-[1000] bg-white border border-slate-200 rounded-lg shadow-md py-1 w-52",
  menuItem: "w-full flex items-center gap-2 text-left px-3 py-1 text-xs text-slate-700 hover:bg-violet-100",
  menuNote: "px-3 py-1 text-[0.625rem] text-slate-400",
};

// "AdA. OBSERVED": AdA's circle (AdaAvatar), the AdA. wordmark + OBSERVED, a
// typing bubble, then the fact chips fading in one by one (animations:
// ada-dot and ada-appear in src/index.css).
export const ADA = {
  row: "shrink-0 flex items-center gap-2 min-w-0 overflow-hidden",
  label: "shrink-0 flex items-center gap-1.5 mr-1",
  // About the height of the small capitals beside it.
  wordmark: "h-[0.75rem] w-auto",
  facts: "flex items-center gap-2 min-w-0 flex-1 overflow-hidden",
  typing: "inline-flex items-center gap-1 bg-white border border-slate-200 rounded-full rounded-bl-sm px-3 py-2",
  dot: "ada-dot w-1.5 h-1.5 rounded-full bg-violet-400",
  appear: "ada-appear",
};

// The brand purple (#7C3AED = Tailwind violet-600): "Your business" tag and the
// pinned advertiser's highlight. Not a channel colour.
export const BRAND_PURPLE = "#7C3AED";

// The pinned advertiser: the purple chip in the tab bar, and highlights on every page.
export const PIN = {
  // Gives way to the tabs: it shrinks (name shortened with "…"), they never do.
  area: "ml-auto flex items-center gap-2 min-w-0",
  chip: "flex items-center gap-1 min-w-0 bg-violet-100 text-violet-800 border border-violet-200 rounded-full pl-2.5 pr-1 py-0.5 text-xs max-w-[18rem]",
  chipIcon: "text-[0.6875rem] shrink-0",
  chipName: "font-semibold truncate min-w-0",
  chipLink: "shrink-0 px-1.5 py-0.5 rounded-full text-violet-500 hover:text-violet-900 hover:bg-violet-200",
  chipClear: "shrink-0 w-5 h-5 flex items-center justify-center rounded-full text-sm leading-none text-violet-500 hover:text-violet-900 hover:bg-violet-200",
  chipEmpty: "flex items-center gap-1.5 border border-dashed border-slate-400 text-slate-600 rounded-full px-2.5 py-0.5 text-xs hover:text-slate-900 whitespace-nowrap",
  share: "shrink-0 w-6 h-6 flex items-center justify-center border border-slate-300 rounded-full text-slate-400 bg-white cursor-not-allowed",
  note: "min-w-0 truncate text-[0.6875rem] text-amber-700",
  // One shade deeper than the row hover (violet-50), so the pinned row stands out.
  row: "bg-violet-100",
  rowFirstCell: "shadow-[inset_3px_0_0_#7C3AED]",
  tag: "text-[0.625rem] font-semibold text-violet-800 bg-violet-100 border border-violet-200 rounded px-1 leading-4 shrink-0",
  card: "ring-2 ring-violet-300 bg-violet-50/40",
  ad: "bg-violet-50 border-violet-200",
};

// Highlighted advertisers (the yellow highlighter in the Advertisers filter):
// a yellow row with a yellow edge on every table, a yellow ring on the Google
// Ads page. The pinned advertiser's purple wins when it's both.
export const HIGHLIGHT = {
  row: "bg-yellow-50",
  rowFirstCell: "shadow-[inset_3px_0_0_#facc15]",
  card: "ring-2 ring-yellow-300 bg-yellow-50/40",
  ad: "bg-yellow-50 border-yellow-200",
  tag: "text-[0.625rem] font-semibold text-yellow-900 bg-yellow-200 rounded px-1 leading-4 shrink-0",
  // The highlighter pen on each row of the Advertisers dropdown (and on
  // "All advertisers", where it clears every highlight).
  mark: "shrink-0 p-0.5 rounded text-slate-400 hover:text-yellow-600 hover:bg-yellow-50 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-400 disabled:cursor-default",
  markOn: "shrink-0 p-0.5 rounded text-yellow-700 bg-yellow-100 hover:bg-yellow-200",
  count: "shrink-0 text-[0.625rem] font-semibold text-yellow-900 bg-yellow-200 rounded px-1 leading-4",
};

// A table row's classes: pinned (purple) beats highlighted (yellow), else hover.
export function advertiserRowClass(isPinned, isHighlighted) {
  if (isPinned) return { row: PIN.row, first: PIN.rowFirstCell };
  if (isHighlighted) return { row: HIGHLIGHT.row, first: HIGHLIGHT.rowFirstCell };
  return { row: "hover:bg-violet-50", first: "" };
}

// Advertiser Overview: the page scrolls, like Market Overview; panels have a
// fixed height and scroll inside themselves.
// Same type scale as Market Overview's tiles: small caps titles (11px), one
// big number per tile (text-4xl), readable lines (text-sm); tables text-sm.
// Channel tiles are sized so all seven channels fit as full tiles at 1440px.
export const ADVERTISER = {
  page: "flex flex-col gap-2.5 pb-4",
  panelRow: "grid grid-cols-2 gap-2.5 h-[18.75rem]",
  panelSlot: "flex flex-col min-h-0 min-w-0",
  gridPanelSlot: "flex flex-col h-[60vh] min-h-[23.75rem]",
  cardRow: "shrink-0 grid grid-cols-4 gap-2.5 h-[8.5rem]",
  cardSlot: "flex flex-col min-h-0 min-w-0",
  card: "h-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 flex flex-col gap-1 min-h-0 min-w-0 overflow-hidden",
  cardDashed: "h-full bg-white border border-dashed border-slate-300 rounded-xl px-4 py-2.5 flex flex-col gap-1 min-h-0 min-w-0 overflow-hidden",
  cardExpanded: "h-full bg-white rounded-xl px-6 py-4 flex flex-col gap-1.5 min-h-0 overflow-auto",
  cardHead: "flex items-center justify-between gap-2 shrink-0 h-5",
  cardTitle: `flex items-center gap-1.5 ${TITLE} truncate`,
  valueRow: "flex items-baseline gap-2 min-w-0",
  big: "text-4xl font-bold text-slate-900 leading-none tracking-tight shrink-0",
  sub: "text-sm text-slate-600 truncate",
  small: "text-xs text-slate-500 truncate",
  strong: "text-sm font-semibold text-slate-800 truncate",
  name: "text-lg font-semibold text-slate-900 truncate leading-tight",
  // Channel tiles, full width, in two titled groups as on Market Overview
  // (Google page layout, Other platforms). Channels with data are full tiles;
  // none seen / not searched are narrow strips that open on hover (STRIP).
  channelRow: "shrink-0 flex gap-2.5 min-w-0",
  channelGroup: "flex flex-col gap-1.5 min-w-0 transition-[flex-grow] duration-500 ease-in-out",
  channelGroupTitle: `${TITLE} truncate px-0.5`,
  channelTiles: "flex gap-2.5 min-w-0 h-[9.375rem]",
  channelSlot: "flex-1 flex flex-col min-h-0 min-w-0",
  // A channel tile with data is filled with the channel's colour (as on
  // Market Overview's Google page layout); the expanded view stays white.
  channelCard: {
    ppc: "h-full bg-blue-50 border border-blue-200 rounded-xl px-4 py-2.5 flex flex-col gap-1 min-h-0 min-w-0 overflow-hidden",
    local: "h-full bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 flex flex-col gap-1 min-h-0 min-w-0 overflow-hidden",
    seo: "h-full bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2.5 flex flex-col gap-1 min-h-0 min-w-0 overflow-hidden",
    shopping: "h-full bg-orange-50 border border-orange-200 rounded-xl px-4 py-2.5 flex flex-col gap-1 min-h-0 min-w-0 overflow-hidden",
    lsa: "h-full bg-rose-50 border border-rose-200 rounded-xl px-4 py-2.5 flex flex-col gap-1 min-h-0 min-w-0 overflow-hidden",
  },
  // A channel's place down Google's page (1, 2, 3...), as on Market Overview's tiles.
  order: "text-[0.6875rem] font-semibold text-slate-500",
  // A channel not searched yet (AI Overview, Bing): "NOT SEARCHED" where the number goes.
  notSearched: "text-sm font-bold text-slate-400 uppercase tracking-wider",
  bars: "flex flex-col gap-1.5",
  barLine: "flex items-baseline justify-between gap-2 text-sm leading-5",
  barLabel: "text-slate-600 truncate",
  barValue: "text-slate-800 font-semibold whitespace-nowrap",
  // "Searches seen in" bars, in the toned-back purple: the pinned advertiser
  // stronger, #1 and Top 3 paler.
  barTrack: "h-1.5 bg-violet-50 rounded-full overflow-hidden",
  barYou: "h-full bg-violet-400 rounded-full",
  barOther: "h-full bg-violet-200 rounded-full",
  devTable: "text-sm border-separate border-spacing-x-3 border-spacing-y-0.5 -mx-3",
  devHead: "text-xs font-normal text-slate-500 text-left truncate max-w-[9.375rem]",
  devLabel: "text-slate-600 text-left font-normal",
  devCell: "text-slate-800 whitespace-nowrap",
  // Compact tables for Where you rank / Who you're up against (fixed columns, no sideways scroll).
  th: "sticky top-0 z-10 bg-white text-xs font-normal text-slate-500 px-2 py-1.5 text-left whitespace-nowrap border-y border-slate-100 overflow-hidden",
  thFirst: "sticky top-0 z-10 bg-white text-xs font-normal text-slate-500 pl-4 pr-2 py-1.5 text-left whitespace-nowrap border-y border-slate-100",
  td: "px-2 py-1.5 text-sm text-slate-700 whitespace-nowrap border-b border-slate-100 overflow-hidden text-ellipsis",
  tdFirst: "pl-4 pr-2 py-1.5 text-sm text-slate-500 whitespace-nowrap border-b border-slate-100",
  divider: "text-center text-[0.625rem] text-slate-400 border-b border-dashed border-slate-300 py-0.5",
  sharedTrack: "w-10 h-1.5 bg-slate-200 rounded-full overflow-hidden shrink-0",
  sharedFill: "h-full bg-slate-700 rounded-full",
  // "Where you show up" grid (styled like Market Overview's Gaps by postcode).
  gridTh: "sticky top-0 z-10 bg-white text-xs font-normal text-slate-500 px-1 py-1.5 text-center overflow-hidden",
  gridThFirst: "sticky top-0 z-10 bg-white text-xs font-normal text-slate-500 pl-4 pr-1 py-1.5 text-left w-32",
  gridRowLabel: "pl-4 pr-1 py-0.5 align-middle",
  gridPostcode: "text-sm font-semibold text-slate-800 leading-tight",
  gridTown: "text-[0.6875rem] text-slate-500 leading-tight truncate",
  gridCell: "px-1 py-0.5",
  legendRow: "shrink-0 flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-1.5 border-t border-slate-100 text-[0.6875rem] text-slate-500",
  legendSwatch: "inline-block w-3 h-3 rounded-sm align-middle mr-1",
  input: "w-full border border-slate-300 rounded-lg px-2.5 py-1.5 text-sm text-slate-700 bg-white focus:outline-none focus:border-slate-500",
  pinButton: "text-xs font-medium border border-slate-300 rounded-md px-2 py-0.5 text-slate-700 hover:bg-violet-100 hover:border-violet-300 hover:text-violet-800",
  pinnedLabel: "text-xs font-semibold text-violet-700",
  modal: "fixed inset-x-0 top-16 mx-auto z-[950] w-[40rem] max-w-[95vw] max-h-[80vh] flex flex-col bg-white rounded-xl shadow-2xl",
  // Link a Google Maps listing: wide enough for its whole table (name, postcodes, linked to, website + Link).
  modalWide: "fixed inset-x-0 top-16 mx-auto z-[950] w-[60rem] max-w-[95vw] max-h-[80vh] flex flex-col bg-white rounded-xl shadow-2xl",
  modalBackdrop: "fixed inset-0 z-[940] bg-slate-900/40",
  smallSelect: "border border-slate-300 rounded-md px-1.5 py-0.5 text-xs text-slate-700 bg-white max-w-[11.25rem]",
  action: "text-xs font-medium text-blue-700 underline underline-offset-2 hover:text-blue-900",
};

// Channel titles as coloured pills (Ads blue, Maps amber, SEO green,
// Shopping orange, Local Services Ads rose, AI Overview sky, Bing teal -
// Scott, 15 Oct 2026). Mixed case, as on Market Overview's tiles.
const CHANNEL_PILL_BASE = "inline-flex items-center px-2 py-0.5 rounded-full text-[0.6875rem] font-semibold shrink-0";
export const CHANNEL_PILL = {
  ppc: `${CHANNEL_PILL_BASE} bg-blue-100 text-blue-900`,
  local: `${CHANNEL_PILL_BASE} bg-amber-100 text-amber-900`,
  seo: `${CHANNEL_PILL_BASE} bg-emerald-100 text-emerald-900`,
  shopping: `${CHANNEL_PILL_BASE} bg-orange-100 text-orange-900`,
  lsa: `${CHANNEL_PILL_BASE} bg-rose-100 text-rose-900`,
  ai: `${CHANNEL_PILL_BASE} bg-sky-100 text-sky-900`,
  bing: `${CHANNEL_PILL_BASE} bg-teal-100 text-teal-900`,
};

// "Where you show up" cells: the Market Overview gaps cell shape and the same
// soft purple scale. Purple = seen (darker = seen in more of the searches); a
// solid purple outline on white = not seen where ads ran; dashed grey = not
// seen, no ads.
export function showUpCellClass(status, value, searches) {
  const base = "h-8 rounded-md text-xs font-semibold flex items-center justify-center whitespace-nowrap border";
  if (searches === 0) return `${base} border-transparent text-slate-300`;
  if (status === "not_seen_ads_ran") return `${base} border-violet-400 bg-white text-violet-800`;
  if (status !== "seen") return `${base} border-dashed border-slate-300 bg-white text-slate-500`;
  const share = value / searches;
  if (share <= 0.25) return `${base} border-violet-100 bg-violet-50 text-slate-800`;
  if (share <= 0.5) return `${base} border-violet-200 bg-violet-100 text-slate-800`;
  if (share <= 0.75) return `${base} border-violet-300 bg-violet-200 text-slate-900`;
  if (share < 1) return `${base} border-violet-300 bg-violet-300 text-slate-900`;
  return `${base} border-violet-500 bg-violet-500 text-white`;
}

// Legend swatches for "Where you show up" (match showUpCellClass).
export const SHOW_UP_LEGEND = {
  seen: "bg-violet-300",
  notSeenAdsRan: "border border-violet-400 bg-white",
  notSeenNoAds: "border border-dashed border-slate-400 bg-white",
};

// Expand-to-full-screen panels.
export const EXPAND = {
  button: "shrink-0 w-6 h-6 flex items-center justify-center rounded-md text-sm text-slate-500 hover:text-slate-900 hover:bg-violet-100",
  open: "fixed inset-6 z-[900] flex flex-col min-h-0 rounded-xl shadow-2xl",
  backdrop: "fixed inset-0 z-[890] bg-slate-900/40",
};

// Market Overview's tile row, straight on the page: Your searches (white
// tiles, purple pills), Google page layout (tiles filled with the channel
// colour, in Google's order; white when none seen), and Other platforms
// (AI Overview, Bing: white tiles saying NOT SEARCHED).
// Pills are mixed case (as on Advertiser Overview) so "Google Shopping" fits
// a tile on a 1440px screen. Sizes are checked against the fullest case: all
// seven channel tiles full width in the two right-hand columns (LSA, AI
// Overview and Bing checked too), ~125px each at 1440px.
const TILE_BASE = "min-w-0 rounded-lg border px-2 py-2 flex flex-col gap-1 overflow-hidden";
const TILE_PILL_BASE = "inline-block max-w-full truncate px-1.5 py-0.5 rounded-full text-[0.6875rem] font-semibold";
export const TILES = {
  // Same columns as OVERVIEW.panelRow, so the tiles line up with the panels.
  row: "shrink-0 grid grid-cols-3 gap-2.5 min-w-0",
  channelSpan: "col-span-2 flex gap-2.5 min-w-0",
  // A group: its small title over its tiles, straight on the page (no panel).
  // Its width (flex-grow) is set by the page from how many tiles it holds.
  group: "flex flex-col gap-1.5 min-w-0 transition-[flex-grow] duration-500 ease-in-out",
  groupTitle: `${TITLE} truncate px-0.5`,
  // A fixed height, so a tile getting narrower (when a strip opens) never
  // makes the row taller and pushes the page down. Fits a two-line foot.
  tiles: "flex gap-2 min-w-0 h-[8.75rem]",
  full: "flex-1",
  fill: {
    summary: `${TILE_BASE} bg-white border-slate-200`,
    ppc: `${TILE_BASE} bg-blue-50 border-blue-200`,
    shopping: `${TILE_BASE} bg-orange-50 border-orange-200`,
    local: `${TILE_BASE} bg-amber-50 border-amber-200`,
    seo: `${TILE_BASE} bg-emerald-50 border-emerald-200`,
    lsa: `${TILE_BASE} bg-rose-50 border-rose-200`,
    // None seen, or not searched yet: white, like an empty Google Shopping tile.
    noneSeen: `${TILE_BASE} bg-white border-slate-200`,
  },
  pill: {
    summary: `${TILE_PILL_BASE} bg-violet-100 text-violet-900`,
    ppc: `${TILE_PILL_BASE} bg-blue-100 text-blue-900`,
    shopping: `${TILE_PILL_BASE} bg-orange-100 text-orange-900`,
    local: `${TILE_PILL_BASE} bg-amber-100 text-amber-900`,
    seo: `${TILE_PILL_BASE} bg-emerald-100 text-emerald-900`,
    lsa: `${TILE_PILL_BASE} bg-rose-100 text-rose-900`,
    ai: `${TILE_PILL_BASE} bg-sky-100 text-sky-900`,
    bing: `${TILE_PILL_BASE} bg-teal-100 text-teal-900`,
  },
  // "NOT SEARCHED" in place of the number (AI Overview, Bing).
  notSearched: "text-base font-bold text-slate-400 uppercase tracking-wider leading-9",
  head: "flex items-center justify-between gap-1.5 min-w-0 h-5",
  order: "text-[0.6875rem] font-semibold text-slate-500 shrink-0",
  valueRow: "flex items-baseline gap-1.5 min-w-0 mt-0.5",
  big: "text-4xl font-bold text-slate-900 leading-none tracking-tight shrink-0",
  // Beside the number, e.g. Multi-channel's "(15%)".
  extra: "text-sm font-semibold text-slate-500 truncate",
  // What the number counts, and the last line: these wrap rather than get cut off.
  unit: "text-sm font-medium text-slate-700 leading-snug",
  foot: "text-xs text-slate-500 leading-snug",};

// Channel tiles with nothing found: a narrow strip with the pill on its side
// like a side tab, read top to bottom; hovering opens it back into a full tile
// (ChannelStrip.jsx). slot animates its width (flex-grow is set by the page);
// STRIP_ANIMATION is shared with the groups so they move together.
const STRIP_ANIMATION = "transition-[flex-grow] duration-500 ease-in-out";
export const STRIP = {
  slot: `flex min-w-0 ${STRIP_ANIMATION}`,
  minWidth: "3rem",
  tile: "flex-1 min-w-0 rounded-lg border bg-white px-1 py-2 flex flex-col items-center overflow-hidden cursor-default",
  solid: "border-slate-200",
  dashed: "border-dashed border-slate-300",
  // writing-mode is set on the element (vertical, top to bottom).
  pill: "max-h-full truncate whitespace-nowrap px-1 py-2.5 rounded-full text-sm font-semibold leading-none",
  pillColour: {
    ppc: "bg-blue-100 text-blue-900",
    shopping: "bg-orange-100 text-orange-900",
    local: "bg-amber-100 text-amber-900",
    seo: "bg-emerald-100 text-emerald-900",
    lsa: "bg-rose-100 text-rose-900",
    ai: "bg-sky-100 text-sky-900",
    bing: "bg-teal-100 text-teal-900",
  },
};

// Channel badges in the table: "searches · average position".
// Solid channel fills (the Audits page's channel mix bar and its legend dots):
// the same hues as the pills, at a mid shade.
export const CHANNEL_FILL = {
  ppc: "bg-blue-500",
  local: "bg-amber-400",
  seo: "bg-emerald-600",
  shopping: "bg-orange-500",
};

export const CHANNEL_BADGE = {
  ppc: "bg-blue-100 text-blue-900",
  local: "bg-amber-100 text-amber-900",
  seo: "bg-emerald-100 text-emerald-900",
  shopping: "bg-orange-100 text-orange-900",
  lsa: "bg-rose-100 text-rose-900",
};

// Channel group headings in the table: channel colour text and underline.
export const CHANNEL_HEAD = {
  ppc: "text-blue-800 border-b-2 border-b-blue-300",
  local: "text-amber-800 border-b-2 border-b-amber-300",
  seo: "text-emerald-800 border-b-2 border-b-emerald-300",
  shopping: "text-orange-800 border-b-2 border-b-orange-300",
  lsa: "text-rose-800 border-b-2 border-b-rose-300",
};

// The brand purple scale (Tailwind violet; 600 is the brand purple #7C3AED).
// Heat scales (Gaps by postcode, Gaps by keyword, Where you show up) use the
// soft end, 50-500, so they show the difference without a wall of purple
// (Scott, 15 Oct 2026: "dial the purple back"). Never used for channels.
export const PURPLE_SCALE = {
  50: "#f5f3ff", 100: "#ede9fe", 200: "#ddd6fe", 300: "#c4b5fd", 400: "#a78bfa", 500: "#8b5cf6",
  600: "#7c3aed", 700: "#6d28d9", 800: "#5b21b6", 900: "#4c1d95",
};

// Competition type: no colour per type (Scott, 15 Oct 2026) - one soft purple
// bar per type showing its share.
export const COMPETITION = {
  total: "flex items-baseline gap-1.5 mt-2",
  totalValue: "text-4xl font-bold text-slate-900 leading-none tracking-tight",
  totalLabel: "text-sm text-slate-600",
  table: "w-full mt-3 text-sm border-separate border-spacing-y-1.5",
  head: "text-xs font-normal text-slate-500",
  label: "text-slate-800 whitespace-nowrap pr-3",
  barCell: "w-full",
  barTrack: "h-2 bg-violet-50 rounded-full overflow-hidden",
  barFill: "h-full bg-violet-300 rounded-full",
  share: "text-right font-semibold text-slate-900 pl-3 whitespace-nowrap",
  count: "text-right text-slate-500 pl-3 whitespace-nowrap",
};

// Gaps cells: white dashed box for none, then the soft purple scale, darker = more.
export function gapCellClass(value, searches) {
  const base = "h-8 rounded-md text-sm font-semibold flex items-center justify-center gap-1 border";
  if (searches === 0) return `${base} border-transparent text-slate-300`;
  if (value === 0) return `${base} border-dashed border-slate-300 bg-white text-slate-800`;
  if (value === 1) return `${base} border-violet-100 bg-violet-50 text-slate-800`;
  if (value === 2) return `${base} border-violet-200 bg-violet-100 text-slate-800`;
  if (value === 3) return `${base} border-violet-200 bg-violet-200 text-slate-800`;
  if (value === 4) return `${base} border-violet-300 bg-violet-300 text-slate-900`;
  if (value === 5) return `${base} border-violet-400 bg-violet-400 text-slate-900`;
  return `${base} border-violet-500 bg-violet-500 text-white`;
}

// "Gaps by keyword" map: postcode areas coloured by how many advertisers
// compete there - the same soft purple scale as the gaps table. Keys match
// competitionLevel(): notSearched, then 0 (none, dashed) to 5 (6+).
// Leaflet needs real colour values, not class names.
const MAP_AREA = { weight: 1.5, color: PURPLE_SCALE[400], fillOpacity: 0.75 };
export const KEYWORD_MAP = {
  levels: {
    notSearched: { weight: 1, color: "#cbd5e1", dashArray: "2 4", fillColor: "#f1f5f9", fillOpacity: 0.5 },
    0: { weight: 2, color: "#475569", dashArray: "5 4", fillColor: "#ffffff", fillOpacity: 0.75 },
    1: { ...MAP_AREA, fillColor: PURPLE_SCALE[100] },
    2: { ...MAP_AREA, fillColor: PURPLE_SCALE[200] },
    3: { ...MAP_AREA, fillColor: PURPLE_SCALE[300] },
    4: { ...MAP_AREA, fillColor: PURPLE_SCALE[400] },
    5: { ...MAP_AREA, color: PURPLE_SCALE[600], fillColor: PURPLE_SCALE[500], fillOpacity: 0.8 },
  },
  legend: [["0", "None"], ["1", "1"], ["2", "2"], ["3", "3"], ["4", "4–5"], ["5", "6+"], ["notSearched", "Not searched"]],
  pointRadius: 12,
  // Title, choices and expand button on one row, so the map gets the room.
  header: "shrink-0 flex items-center gap-2 px-4 py-2 min-w-0",
  controls: "flex-1 min-w-0 flex items-center justify-end gap-2",
  keywordSelect: "min-w-0 max-w-[45%] border border-slate-200 rounded-md px-1.5 py-1 text-xs text-slate-700 bg-white focus:outline-none focus:border-slate-400",
  // isolate: keeps Leaflet's own layers (z-index 400-1000) from covering menus outside the map.
  mapArea: "relative isolate flex-1 min-h-0 border-t border-slate-100",
  overlay: "absolute inset-0 z-[500] flex items-center justify-center bg-white/70 text-xs text-slate-500",
  legendRow: "shrink-0 flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-1.5 border-t border-slate-100 text-[0.6875rem] text-slate-500",
  swatch: "inline-block w-3 h-3 rounded-sm align-middle mr-1",
  label: "!bg-white/90 !border-slate-200 !text-slate-800 !text-[0.6875rem] !font-semibold !px-1.5 !py-0.5 !shadow-sm",
  popupTitle: "text-sm font-semibold text-slate-900",
  popupMuted: "text-[0.6875rem] text-slate-500",
  popupRow: "flex justify-between gap-4 text-xs text-slate-700",
};

export const GAPS = {
  toggle: "flex border border-slate-200 rounded-md overflow-hidden bg-white",
  toggleButton: "px-3 py-1 text-xs font-medium text-slate-600 hover:text-slate-900",
  toggleActive: "px-3 py-1 text-xs font-semibold bg-violet-100 text-violet-800",
  summaryRow: "shrink-0 flex items-center justify-between gap-2 px-4 pt-1.5 pb-1 text-xs text-slate-500 border-t border-slate-100",
  summaryStrong: "font-semibold text-slate-900",
  sort: "text-xs text-slate-500 bg-transparent border-none focus:outline-none cursor-pointer text-right",
  th: "sticky top-0 z-10 bg-white text-xs font-normal text-slate-500 px-1 py-1 text-center max-w-[6rem] truncate",
  thFirst: "sticky top-0 z-10 bg-white text-xs font-normal text-slate-500 px-4 py-1 text-left",
  rowLabel: "px-4 py-0.5 text-sm font-semibold text-slate-800 whitespace-nowrap",
  cell: "px-1 py-0.5",
  emptyText: "text-[0.625rem] font-normal text-slate-500",
};
